'use client';

import { useState, useEffect } from 'react';
import { Box, Button, Switch, Typography } from '@mui/material';
import { useRouter } from 'next/navigation';
import { useMutation } from '@tanstack/react-query';
import Sms from '@/components/icons/Sms';
import {
  NotificationPreferences,
  NotificationPreferencesOptInStatusEnum,
} from '@/entities/preferences/notification';
import { updateUserPreferences } from '@/hooks/useUpdateUserPreferences';
import { PreferenceDomain } from '@/entities/preferences';
import { UserProfile } from '@/services/customerProfileService.types';
import useTitanAnalytics from '@/hooks/useTitanAnalytics';
import NotificationIcon from './_icon';
import Section from '../Section';

interface Props {
  user?: UserProfile;
  preferences?: NotificationPreferences;
}

export interface UpdateNotificationPayload {
  phoneNumber?: string;
  status?: NotificationPreferencesOptInStatusEnum;
}

export const prepareUpdateNotificationPreferencesPayload = (
  { phoneNumber, status }: UpdateNotificationPayload,
  preferences?: NotificationPreferences,
): NotificationPreferences | undefined => {
  // If the preference opt-ins already exists
  if (preferences) {
    const newPreferences = {
      ...preferences,
    };

    newPreferences['opt-ins'] = newPreferences['opt-ins'].map((optin) => {
      if (optin.level === 'network') {
        return {
          ...optin,
          preferences: optin.preferences.map((pref) => {
            if (pref.channel === 'sms') {
              return {
                ...pref,
                status: status ?? 'inactive',
              };
            }

            return { ...pref };
          }),
        };
      }

      return {
        ...optin,
      };
    });

    return newPreferences;
  }

  // Preference does not exist and we need to add a new optin for phone
  if (phoneNumber) {
    return {
      'opt-ins': [
        {
          level: 'network',
          preferences: [
            {
              channel: 'sms',
              contact: phoneNumber,
              status: status ?? 'inactive',
            },
          ],
        },
      ],
    };
  }

  // Preference does not exist and we don't have a phone number
  return;
};

export const getSmsNotificationStatus = (
  preferences?: NotificationPreferences,
): NotificationPreferencesOptInStatusEnum => {
  let flag: NotificationPreferencesOptInStatusEnum = 'inactive';

  preferences?.['opt-ins']?.forEach((optin) => {
    if (optin?.level === 'network') {
      optin.preferences.forEach((pref) => {
        if (pref?.channel === 'sms') {
          flag = pref.status ?? 'inactive';
        }
      });
    }
  });

  return flag;
};

const label = { inputProps: { 'aria-label': 'Switch demo' } };

export default function Notifications({ user, preferences }: Props) {
  const [isChecked, setIsChecked] = useState(false);
  const router = useRouter();
  const titan = useTitanAnalytics();

  const { mutate } = useMutation({
    mutationFn: updateUserPreferences,
  });

  useEffect(() => {
    const savedStatus = getSmsNotificationStatus(preferences);

    setIsChecked(savedStatus === 'active');
  }, [preferences]);

  const onToggle = async () => {
    const newPreferences = prepareUpdateNotificationPreferencesPayload(
      {
        phoneNumber: user?.primary_phone_number?.value,
        status: isChecked ? 'inactive' : 'active',
      },
      preferences,
    );

    if (newPreferences) {
      mutate({
        product: PreferenceDomain.Notification,
        body: newPreferences,
      });
    }

    titan
      .track({
        userId: user?.profile_id,
        event: 'product_clicked',
        channel: 'web',
        type: 'track',
        context: {},
        properties: {
          sku: 'Notifications',
          variant: isChecked ? 'inactive' : 'active',
          narvar_product_context: 'track',
        },
      })
      // eslint-disable-next-line no-console
      .catch((e) => console.error(e));
    setIsChecked(!isChecked);
  };

  const isPhoneAdded = !!user?.primary_phone_number?.value;

  return (
    <Section
      Icon={NotificationIcon}
      title="Notifications"
      desc="Manage how you receive messages from retailers on the status of your orders."
      style={{ marginTop: '2rem' }}
    >
      <Box mt={1}>
        <Box mt={4} display="flex">
          <Box margin="auto">
            <Sms />
          </Box>
          <Box ml={2} flex={1} pr={2}>
            <Typography variant="h6">SMS</Typography>

            {isPhoneAdded ? (
              <Typography variant="body1">
                Receive delivery notifications via SMS for all orders
              </Typography>
            ) : (
              <Typography variant="body2">
                Phone number is needed to manage delivery notifications.{' '}
              </Typography>
            )}

            {!isPhoneAdded ? (
              <Button
                sx={{ mt: 0.5 }}
                variant="text"
                color="secondary"
                onClick={() => router.push('/setup-profile/phone')}
                style={{ padding: 0 }}
              >
                Add Phone Number
              </Button>
            ) : null}
          </Box>
          <Box margin="auto">
            {isPhoneAdded ? (
              <Switch
                {...label}
                style={{ marginRight: 2 }}
                checked={isChecked}
                onClick={onToggle}
                color="secondary"
              />
            ) : null}
          </Box>
        </Box>
      </Box>
    </Section>
  );
}
